<template>
	<!-- Ohne `color`: Exportieren ist weder Information noch Erfolg noch Fehler.
	     Das Blau stand hier neben einem gruenen „Export" und einem roten
	     „Hausverwalter" in derselben Werkzeugleiste — drei Farben, drei
	     angedeutete Bedeutungen, keine davon echt, und fuer rot-gruen-blinde
	     Nutzer waren zwei davon derselbe Knopf. Neutral laeuft der Knopf auf
	     der getoenten Struktur-Stufe mit. -->
	<ButtonIcon
		icon="fal fa-file-export"
		text="component.export.exportLabel"
		:disabled="disabled"
		:aria-label="t('component.export.exportLabel')"
		@click.stop="open"
	></ButtonIcon>

	<v-dialog v-model="dialog" max-width="480" persistent>
		<v-card>
			<v-card-title class="text-h6">
				{{ t('component.export.dialogTitle') }}
			</v-card-title>

			<v-card-text>
				<div class="text-body-2 text-medium-emphasis mb-1">
					{{ t('component.export.format.label') }}
				</div>
				<v-radio-group
					v-model="format"
					hide-details
					density="comfortable"
					inline
				>
					<v-radio
						:value="'pdf'"
						:label="t('component.export.format.pdf')"
					></v-radio>
					<v-radio
						:value="'csv'"
						:label="t('component.export.format.csv')"
					></v-radio>
				</v-radio-group>

				<v-divider class="my-3"></v-divider>

				<v-radio-group v-model="scope" hide-details density="comfortable">
					<v-radio v-if="selectedCount > 0" :value="'selected'">
						<template #label>
							<span>
								{{ t('component.export.scope.selected') }}
								<span class="text-medium-emphasis ml-1"
									>({{ selectedCount }})</span
								>
							</span>
						</template>
					</v-radio>
					<v-radio :value="'page'">
						<template #label>
							<span>
								{{ t('component.export.scope.currentPage') }}
								<span class="text-medium-emphasis ml-1">({{ pageCount }})</span>
							</span>
						</template>
					</v-radio>
					<v-radio :value="'all'" :disabled="totalCount <= 0">
						<template #label>
							<span>
								{{ t('component.export.scope.all') }}
								<span class="text-medium-emphasis ml-1"
									>({{ totalCount }})</span
								>
							</span>
						</template>
					</v-radio>
				</v-radio-group>

				<v-alert
					v-if="scope === 'all' && totalCount > LARGE_THRESHOLD"
					density="compact"
					type="warning"
					variant="tonal"
					class="mt-3"
				>
					{{ t('component.export.largeWarning', { count: totalCount }) }}
				</v-alert>

				<template v-if="format === 'pdf'">
					<v-divider class="my-3"></v-divider>

					<v-checkbox
						v-model="includeNotes"
						density="compact"
						hide-details
						color="primary"
					>
						<template #label>
							<span>{{ t('component.export.includeNotes.label') }}</span>
						</template>
					</v-checkbox>
					<div class="text-caption text-medium-emphasis ml-8 mt-n1">
						{{ t('component.export.includeNotes.hint') }}
					</div>

					<template v-if="hasGroups">
						<v-checkbox
							v-model="groupOnNewPage"
							density="compact"
							hide-details
							color="primary"
							class="mt-2"
						>
							<template #label>
								<span>{{ t('component.export.groupOnNewPage.label') }}</span>
							</template>
						</v-checkbox>
						<div class="text-caption text-medium-emphasis ml-8 mt-n1">
							{{ t('component.export.groupOnNewPage.hint') }}
						</div>
					</template>

					<template v-if="hasSubtable">
						<v-checkbox
							v-model="includeSubtables"
							density="compact"
							hide-details
							color="primary"
							class="mt-2"
						>
							<template #label>
								<span>{{ t('component.export.includeSubtables.label') }}</span>
							</template>
						</v-checkbox>
						<div class="text-caption text-medium-emphasis ml-8 mt-n1">
							{{ t('component.export.includeSubtables.hint') }}
						</div>
					</template>
				</template>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn variant="text" @click="dialog = false" :disabled="busy">
					{{ t('common.cancel.name') }}
				</v-btn>
				<v-btn color="primary" :loading="busy" @click="confirm">
					{{
						format === 'pdf'
							? t('component.export.pdf')
							: t('component.export.csv')
					}}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTableExport, type ExportInput } from '@/composables/useTableExport'
import { useToastStore } from '@/stores/system'
import ButtonIcon from '@/components/templates/Base/ButtonIcon.vue'

export type ExportScope = 'page' | 'all' | 'selected'

interface Props {
	pageCount: number
	totalCount: number
	selectedCount?: number
	disabled?: boolean
	hasGroups?: boolean
	hasSubtable?: boolean
	getExportData: (scope: ExportScope) => Promise<ExportInput> | ExportInput
}

const props = withDefaults(defineProps<Props>(), {
	disabled: false,
	selectedCount: 0,
	hasGroups: false,
	hasSubtable: false,
})

const LARGE_THRESHOLD = 500

const { t } = useI18n()
const { exportToPdf, exportToCsv } = useTableExport()
const toastStore = useToastStore()

const dialog = ref(false)
const format = ref<'pdf' | 'csv'>('pdf')
const scope = ref<ExportScope>('page')
const includeNotes = ref(false)
const groupOnNewPage = ref(false)
const includeSubtables = ref(false)
const busy = ref(false)

function open() {
	if (props.selectedCount > 0) {
		scope.value = 'selected'
	} else {
		scope.value =
			props.totalCount > 0 && props.totalCount <= LARGE_THRESHOLD
				? 'all'
				: 'page'
	}
	format.value = 'pdf'
	includeNotes.value = false
	groupOnNewPage.value = false
	includeSubtables.value = false
	dialog.value = true
}

async function confirm() {
	busy.value = true
	try {
		const input = await props.getExportData(scope.value)
		if (format.value === 'csv') {
			await exportToCsv(input)
		} else {
			await exportToPdf({
				...input,
				includeNotes: includeNotes.value,
				groupPageBreak: groupOnNewPage.value ? 'always' : 'auto',
				includeSubtables: includeSubtables.value,
			})
		}
		dialog.value = false
	} catch (err) {
		console.error('Export failed:', err)
		toastStore.toastError?.({ message: t('component.export.failed') })
	} finally {
		busy.value = false
	}
}
</script>
